// Keycloak Authentication Service
// Uses keycloak-js SDK with redirect flow (Authorization Code + PKCE)
// All Keycloak traffic routed through Kong gateway (/auth/*)

import Keycloak from 'keycloak-js';
import { takeStagedLogin } from './staged-login';

const REALM = 'didi-clients';
const CLIENT_ID = 'didi-web-app';

// Keycloak SSO public — see project_kong_migration_2026_04 / SSO migration 2026-04-29.
// Override at build time via VITE_KEYCLOAK_URL if needed.
const keycloakUrl =
  (import.meta.env.VITE_KEYCLOAK_URL as string | undefined) ||
  'https://sso.caitech.ro';

// Singleton Keycloak instance
const keycloak = new Keycloak({
  url: keycloakUrl,
  realm: REALM,
  clientId: CLIENT_ID,
});

// Listeners notified when token refresh fails (session expired)
type SessionExpiredListener = () => void;

export class KeycloakService {
  static instance = keycloak;
  private static _initialized = false;
  private static _initPromise: Promise<boolean> | null = null;
  private static _refreshPromise: Promise<boolean> | null = null;
  private static _sessionExpiredListeners: SessionExpiredListener[] = [];

  /** Register a callback for when token refresh fails irrecoverably */
  static onSessionExpired(listener: SessionExpiredListener): () => void {
    this._sessionExpiredListeners.push(listener);
    return () => {
      this._sessionExpiredListeners = this._sessionExpiredListeners.filter(l => l !== listener);
    };
  }

  private static _notifySessionExpired(): void {
    this._sessionExpiredListeners.forEach(l => l());
  }

  /**
   * Initialize Keycloak — call once on app startup.
   * Uses check-sso to silently detect existing sessions without forcing login.
   */
  static async init(): Promise<boolean> {
    if (this._initialized) {
      return !!keycloak.authenticated;
    }

    // Prevent multiple concurrent init calls
    if (this._initPromise) {
      return this._initPromise;
    }

    // Demo magic-login (workshop only): ?du=<email>&dp=<password> in the URL
    // triggers a silent Direct-Grant (ROPC) login. STRICTLY gated — runs only
    // when BOTH params are present, so normal users never hit this branch.
    const params = new URLSearchParams(window.location.search);
    const du = params.get('du');
    const dp = params.get('dp');
    // /join staged a one-time credential login (never in the URL, consumed on first read).
    const staged = takeStagedLogin();
    this._initPromise = staged
      ? this._magicInit(staged.u, staged.p)
      : du && dp ? this._magicInit(du, dp) : this._normalInit();

    return this._initPromise;
  }

  /** Wire auto-refresh: refresh before expiry, retry once, then notify UI (no redirect). */
  private static _wireTokenRefresh(): void {
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        setTimeout(() => {
          keycloak.updateToken(5).catch(() => {
            console.warn('Token refresh failed after retry — notifying UI');
            KeycloakService._notifySessionExpired();
          });
        }, 2000);
      });
    };
  }

  /** Standard startup — check-sso silently detects an existing session. */
  private static _normalInit(): Promise<boolean> {
    return keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,
      pkceMethod: 'S256',
      messageReceiveTimeout: 3000, // fail fast on iframe timeout (default 10s)
    }).then((authenticated) => {
      this._initialized = true;
      this._wireTokenRefresh();
      return authenticated;
    });
  }

  /**
   * Demo-only silent login via Direct Access Grant (ROPC). Fetches tokens with
   * the demo credentials from the QR, then boots keycloak-js already-authenticated.
   * On any failure, scrubs the URL and falls back to the normal check-sso flow.
   */
  private static async _magicInit(du: string, dp: string): Promise<boolean> {
    let initCalled = false;
    try {
      const res = await fetch(
        `${keycloakUrl}/realms/${REALM}/protocol/openid-connect/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'password',
            client_id: CLIENT_ID,
            username: du,
            password: dp,
            scope: 'openid',
          }),
        },
      );
      if (!res.ok) throw new Error(`ROPC token request failed: ${res.status}`);
      const tok = await res.json();

      // keycloak-js init() with tokens immediately does a forced updateToken(-1)
      // refresh and THROWS if that refresh fails (e.g. CORS on the refresh call),
      // even though the access token has already been adopted. Swallow that exact
      // case — the access token is valid (~1h), which is plenty for a demo session.
      try {
        initCalled = true;
        await keycloak.init({
          token: tok.access_token,
          refreshToken: tok.refresh_token,
          idToken: tok.id_token,
          checkLoginIframe: false,
        });
      } catch (initErr) {
        if (!keycloak.token) throw initErr; // token was cleared → genuine failure
        console.warn('Magic-login: forced refresh failed, continuing with valid access token:', initErr);
      }

      this._initialized = true;
      this._wireTokenRefresh();

      // Scrub credentials from the URL (history/share safety).
      window.history.replaceState({}, document.title, window.location.pathname);
      return !!keycloak.token;
    } catch (e) {
      console.warn('Password login failed:', e);
      window.history.replaceState({}, document.title, window.location.pathname);
      // keycloak.init() was never reached (e.g. ROPC refused) → the instance is
      // still un-inited, so the normal check-sso flow is safe and keeps Login working.
      if (!initCalled) return this._normalInit();
      // A Keycloak instance can be init'd only once, so we must NOT fall back to
      // _normalInit() here (that would throw). Mark initialized-unauthenticated —
      // the app shows the landing page and the user can log in manually.
      this._initialized = true;
      return false;
    }
  }

  /**
   * Redirect to Keycloak login page.
   * After login, user is redirected back to redirectUri.
   */
  static login(redirectUri?: string): void {
    keycloak.login({
      redirectUri: redirectUri || `${window.location.origin}/dashboard`,
    });
  }

  /**
   * Redirect to Keycloak registration page.
   * After registration + email verification, user is redirected back.
   */
  static register(redirectUri?: string): void {
    keycloak.register({
      redirectUri: redirectUri || `${window.location.origin}/dashboard`,
    });
  }

  /**
   * Redirect to Keycloak password change form.
   * After changing password, user is redirected back.
   */
  static changePassword(redirectUri?: string): void {
    keycloak.login({
      action: 'UPDATE_PASSWORD',
      redirectUri: redirectUri || `${window.location.origin}/dashboard`,
    });
  }

  /**
   * Logout — end Keycloak session and redirect to landing page.
   */
  static logout(): void {
    keycloak.logout({
      redirectUri: `${window.location.origin}/`,
    });
  }

  /**
   * Check if user is currently authenticated (has valid token).
   */
  static isAuthenticated(): boolean {
    return !!keycloak.authenticated;
  }

  /**
   * Refresh the token if it expires within 60 seconds.
   * Used when returning from background/idle — avoids unnecessary refreshes.
   * Returns true if token is valid, false if session is dead.
   */
  static async refreshToken(minValidity: number = 60): Promise<boolean> {
    if (!keycloak.authenticated && !keycloak.refreshToken) return false;

    try {
      await keycloak.updateToken(minValidity);
      return !!keycloak.authenticated;
    } catch {
      return false;
    }
  }

  /**
   * Get the current access token.
   * Attempts to refresh if token is about to expire (< 30 seconds).
   */
  static async getValidToken(): Promise<string | null> {
    if (!keycloak.authenticated) return null;

    try {
      // Deduplicate concurrent refresh calls — all callers share the same promise
      if (!this._refreshPromise) {
        this._refreshPromise = keycloak.updateToken(30).finally(() => {
          this._refreshPromise = null;
        });
      }
      await this._refreshPromise;
      return keycloak.token || null;
    } catch {
      return null;
    }
  }

  /**
   * Get the current access token synchronously (no refresh attempt).
   */
  static getAccessToken(): string | null {
    if (!keycloak.authenticated) return null;
    return keycloak.token || null;
  }

  /**
   * Get Authorization header for API requests.
   * Returns empty object if not authenticated.
   */
  static getAuthHeader(): { Authorization: string } | Record<string, never> {
    if (!keycloak.authenticated || !keycloak.token) return {};
    return { Authorization: `Bearer ${keycloak.token}` };
  }

  /**
   * Get parsed token claims (user info from JWT).
   */
  static getTokenParsed() {
    return keycloak.tokenParsed || null;
  }

  /**
   * Get Keycloak user profile from token.
   */
  static getUserFromToken() {
    const parsed = keycloak.tokenParsed;
    if (!parsed) return null;

    return {
      sub: parsed.sub || '',
      email: (parsed as any).email || '',
      name: (parsed as any).name || '',
      preferred_username: (parsed as any).preferred_username || '',
      given_name: (parsed as any).given_name || '',
      family_name: (parsed as any).family_name || '',
    };
  }
}
